// ─── Knowledge Capture Pipeline — Orchestrator ───────────────────────────────
// One pipeline for every capture source. Runs the five live stages
// (Capture → Extract → Understand → Organize → Store) as background jobs
// with real progress, real AI, and graceful failure. The UI never freezes:
// jobs run async with bounded concurrency and publish snapshots via
// CustomEvents (KNOWLEDGE_EVENTS.jobs).
//
// Server work (CORS fetches, transcription, vision, analysis) goes through
// /api/knowledge/* — see artifacts/api-server/src/routes/knowledge.ts.

import type {
  CaptureJob,
  Flashcard,
  KnowledgeContentType,
  KnowledgeItem,
  KnowledgeSourceType,
  PipelineStage,
} from '@/types/knowledgeItem';
import { AICostTracker } from '@/services/ai/AICostTracker';
import { KNOWLEDGE_EVENTS, countWords, estimateReadingTimeMin } from '@/types/knowledgeItem';
import {
  createCollection,
  findItemBySourceHash,
  generateKnowledgeId,
  getKnowledgeBlob,
  getKnowledgeBlobFromR2,
  getKnowledgeItem,
  listCollections,
  putKnowledgeBlob,
  putKnowledgeBlobToR2,
  putKnowledgeItem,
  updateKnowledgeItem,
} from './KnowledgeStore';
import {
  MAX_FILE_BYTES,
  blobToDataUrl,
  detectFileKind,
  extractDocx,
  extractEpub,
  extractPdf,
  extractPlainText,
  extractSheet,
  fileExtension,
  imageToDataUrl,
  makeImageThumbnail,
  sha256Hex,
} from './KnowledgeExtractors';
import { getSmartCollections } from './RoamerCollectionsService';
import { enqueueForIntelligence } from './vault/RoamerIntelligenceEngine';

// ─── Job state ────────────────────────────────────────────────────────────────

const MAX_CONCURRENT = 2;
const MAX_JOBS_KEPT = 25;

let jobs: CaptureJob[] = [];
let activeCount = 0;
const pending: Array<{ job: CaptureJob; run: (job: CaptureJob) => Promise<void> }> = [];

function emitJobs(): void {
  window.dispatchEvent(
    new CustomEvent(KNOWLEDGE_EVENTS.jobs, { detail: { jobs: getJobsSnapshot() } }),
  );
}

export function getJobsSnapshot(): CaptureJob[] {
  return jobs.map(j => ({ ...j }));
}

export function dismissJob(id: string): void {
  jobs = jobs.filter(j => j.id !== id);
  emitJobs();
}

export function getActiveJobCount(): number {
  return jobs.filter(j => !['done', 'error', 'duplicate'].includes(j.stage)).length;
}

function newJob(label: string, sourceType: KnowledgeSourceType): CaptureJob {
  const job: CaptureJob = {
    id: generateKnowledgeId('job'),
    label: label.length > 70 ? `${label.slice(0, 67)}…` : label,
    sourceType,
    stage: 'capture',
    pct: 0,
    detail: 'Queued…',
    error: null,
    itemId: null,
    startedAt: new Date().toISOString(),
    finishedAt: null,
  };
  jobs.unshift(job);
  // Cap history: drop oldest finished jobs first.
  if (jobs.length > MAX_JOBS_KEPT) {
    const finished = jobs.filter(j => ['done', 'error', 'duplicate'].includes(j.stage));
    const toDrop = new Set(finished.slice(-(jobs.length - MAX_JOBS_KEPT)).map(j => j.id));
    jobs = jobs.filter(j => !toDrop.has(j.id));
  }
  emitJobs();
  return job;
}

function advance(job: CaptureJob, stage: PipelineStage, pct: number, detail: string): void {
  job.stage = stage;
  job.pct = Math.min(99, Math.round(pct));
  job.detail = detail;
  emitJobs();
}

function finishJob(job: CaptureJob, itemId: string | null, detail: string): void {
  job.stage = 'done';
  job.pct = 100;
  job.detail = detail;
  job.itemId = itemId;
  job.finishedAt = new Date().toISOString();
  emitJobs();
}

function duplicateJob(job: CaptureJob, existing: KnowledgeItem): void {
  job.stage = 'duplicate';
  job.pct = 100;
  job.detail = `Already in your Backpack: “${existing.title.slice(0, 50)}”`;
  job.itemId = existing.id;
  job.finishedAt = new Date().toISOString();
  emitJobs();
}

function failJob(job: CaptureJob, error: unknown, itemId: string | null = null): void {
  job.stage = 'error';
  job.error = error instanceof Error ? error.message : String(error);
  job.detail = 'Failed';
  job.itemId = itemId;
  job.finishedAt = new Date().toISOString();
  emitJobs();
}

// ─── In-flight dedupe lock ────────────────────────────────────────────────────
// findItemBySourceHash is check-then-insert; with concurrency 2 the same
// source captured twice at once would slip past it. Claiming the hash up
// front closes the race; locks are released when the job's runner settles.

const inFlightHashes = new Set<string>();
const jobHashLocks = new Map<string, string>();

/** Dedupe against stored items AND in-flight jobs. Returns false (and finalizes the job) when this source is already handled. */
async function claimSourceHash(job: CaptureJob, hash: string): Promise<boolean> {
  const existing = await findItemBySourceHash(hash);
  if (existing) {
    duplicateJob(job, existing);
    return false;
  }
  if (inFlightHashes.has(hash)) {
    job.stage = 'duplicate';
    job.pct = 100;
    job.detail = 'This item is already being captured in another job.';
    job.finishedAt = new Date().toISOString();
    emitJobs();
    return false;
  }
  inFlightHashes.add(hash);
  jobHashLocks.set(job.id, hash);
  return true;
}

function releaseSourceHash(jobId: string): void {
  const hash = jobHashLocks.get(jobId);
  if (hash) {
    inFlightHashes.delete(hash);
    jobHashLocks.delete(jobId);
  }
}

function enqueue(job: CaptureJob, run: (job: CaptureJob) => Promise<void>): void {
  pending.push({ job, run });
  drainQueue();
}

function drainQueue(): void {
  while (activeCount < MAX_CONCURRENT && pending.length > 0) {
    const next = pending.shift()!;
    activeCount++;
    next
      .run(next.job)
      .catch(err => failJob(next.job, err))
      .finally(() => {
        releaseSourceHash(next.job.id);
        activeCount--;
        drainQueue();
      });
  }
}

// ─── Server API helper ────────────────────────────────────────────────────────

async function api<T>(path: string, body: unknown, timeoutMs = 120_000): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/knowledge/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(timeoutMs),
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'TimeoutError') {
      throw new Error('The server took too long to respond. Please try again.');
    }
    throw new Error('Could not reach the WORKROAMER server. Check your connection.');
  }
  const json = (await res.json().catch(() => ({}))) as { error?: string } & T;
  if (!res.ok) throw new Error(json.error ?? `Server error (${res.status})`);
  return json;
}

interface AnalyzeResponse {
  summary: string;
  keyPoints: string[];
  insights: string[];
  quotes: string[];
  actionItems: string[];
  flashcards: Flashcard[];
  suggestedMissions: string[];
  suggestedOpportunities: string[];
  tags: string[];
  categories: string[];
  suggestedCollections: string[];
  searchIndex: string[];
  language: string;
}

// ─── Shared stages ────────────────────────────────────────────────────────────

function buildBaseItem(params: {
  sourceType: KnowledgeSourceType;
  contentType: KnowledgeContentType;
  title: string;
  originalSource: string | null;
  rawContent: string;
}): KnowledgeItem {
  const wordCount = countWords(params.rawContent);
  return {
    id: generateKnowledgeId(),
    title: params.title,
    sourceType: params.sourceType,
    contentType: params.contentType,
    originalSource: params.originalSource,
    publisher: null,
    rawContent: params.rawContent,
    contentHtml: null,
    summary: null,
    keyPoints: [],
    insights: [],
    quotes: [],
    actionItems: [],
    completedActions: [],
    flashcards: [],
    suggestedMissions: [],
    suggestedOpportunities: [],
    tags: [],
    categories: [],
    collections: [],
    searchIndex: [],
    embeddings: null,
    language: 'en',
    thumbnailUrl: null,
    fileName: null,
    fileSize: null,
    fileMimeType: null,
    fileType: null,
    pageCount: null,
    durationSec: null,
    wordCount,
    readingTimeMin: estimateReadingTimeMin(wordCount),
    processingStatus: 'processing',
    processingError: null,
    storageLocation: 'none',
    sourceHash: null,
    favorite: false,
    dateAdded: new Date().toISOString(),
    processedAt: null,
    // ── Roamer Intelligence Engine™ — populated after capture completes ──────
    recognizedCompanies: [],
    recognizedPeople: [],
    recognizedPlaces: [],
    detectedSkills: [],
    detectedCareerTypes: [],
    topicsAi: [],
    keywords: [],
    relatedContent: [],
    intelligenceStatus: 'pending',
    intelligenceStage: null,
    intelligenceProcessedAt: null,
    rememberForMe: false,
  };
}

async function collectionNamesForPrompt(): Promise<string[]> {
  const user = (await listCollections()).map(c => c.name);
  let smart: string[] = [];
  try {
    smart = getSmartCollections().filter(c => c.count > 0).map(c => c.name);
  } catch { /* smart collections are optional context */ }
  return Array.from(new Set([...user, ...smart]));
}

/**
 * Understand + Organize + Store for an assembled item.
 * On AI failure the item is still stored (searchable raw content) with
 * processingStatus 'failed' so the user can retry from the detail view.
 */
async function understandOrganizeStore(
  job: CaptureJob,
  item: KnowledgeItem,
  originalBlob: Blob | null,
): Promise<void> {
  const minChars = 40;
  let aiFailed: string | null = null;

  if (item.rawContent.trim().length >= minChars) {
    advance(job, 'understand', 50, 'Roamer is reading and summarizing…');
    try {
      const existingCollections = await collectionNamesForPrompt();
      const inputTokens = AICostTracker.estimateTokens(item.rawContent + item.title);
      const t0 = Date.now();
      const ai = await api<AnalyzeResponse>('analyze', {
        title: item.title,
        sourceType: item.contentType,
        text: item.rawContent,
        existingCollections,
      });
      AICostTracker.logRequest({
        layer: 2,
        operation: 'analyze',
        itemId: item.id,
        estimatedInputTokens: inputTokens,
        estimatedOutputTokens: 600,
        status: 'complete',
        cacheHit: false,
        durationMs: Date.now() - t0,
        provider: 'workroamer-default',
      });

      item.summary = ai.summary;
      item.keyPoints = ai.keyPoints;
      item.insights = ai.insights;
      item.quotes = ai.quotes;
      item.actionItems = ai.actionItems;
      item.flashcards = ai.flashcards;
      item.suggestedMissions = ai.suggestedMissions;
      item.suggestedOpportunities = ai.suggestedOpportunities;
      item.tags = ai.tags;
      item.categories = ai.categories;
      item.searchIndex = ai.searchIndex;
      item.language = ai.language || 'en';

      advance(job, 'organize', 78, 'Filing into collections…');
      const ids: string[] = [];
      for (const name of ai.suggestedCollections.slice(0, 3)) {
        try {
          const col = await createCollection(name, { aiSuggested: true });
          ids.push(col.id);
        } catch { /* collection creation is best-effort */ }
      }
      item.collections = Array.from(new Set([...item.collections, ...ids]));
    } catch (err) {
      aiFailed = err instanceof Error ? err.message : String(err);
      AICostTracker.logRequest({
        layer: 2,
        operation: 'analyze',
        itemId: item.id,
        estimatedInputTokens: AICostTracker.estimateTokens(item.rawContent),
        estimatedOutputTokens: 0,
        status: 'failed',
        cacheHit: false,
        durationMs: 0,
        provider: 'workroamer-default',
        errorMessage: aiFailed ?? undefined,
      });
    }
  } else {
    advance(job, 'organize', 78, 'Too short for AI analysis — storing as-is');
  }

  advance(job, 'store', 90, 'Saving to your Backpack…');

  if (originalBlob) {
    // Try R2 first for durable cloud storage; fall back to IndexedDB; then none
    let stored = false;
    try {
      const r2Id = await putKnowledgeBlobToR2(item.id, originalBlob);
      item.r2FileId = r2Id;
      item.storageLocation = 'r2';
      stored = true;
    } catch { /* R2 unavailable — try IndexedDB */ }
    if (!stored) {
      try {
        await putKnowledgeBlob(item.id, originalBlob);
        item.storageLocation = 'indexeddb';
      } catch {
        item.storageLocation = 'none'; // quota exceeded — keep text-only copy
      }
    }
  }

  item.processingStatus = aiFailed ? 'failed' : 'complete';
  item.processingError = aiFailed;
  item.processedAt = new Date().toISOString();
  await putKnowledgeItem(item);

  if (aiFailed) {
    job.itemId = item.id;
    failJob(job, `Saved, but AI analysis failed: ${aiFailed}`, item.id);
  } else {
    finishJob(job, item.id, 'Added to your Backpack');
    // Hand off to the Roamer Intelligence Engine™ for deep enrichment
    try { enqueueForIntelligence(item); } catch { /* non-fatal */ }
  }
}

/**
 * Persist the item shell + original blob BEFORE an expensive extraction
 * (transcription / OCR), then run it. A failure never loses the user's
 * capture: the item is kept as 'failed' with the blob stored, so Retry in
 * the detail view can re-run extraction later.
 * Returns null when extraction failed (the job is already finalized).
 */
async function extractWithSafetyNet<T>(
  job: CaptureJob,
  item: KnowledgeItem,
  blob: Blob,
  extract: () => Promise<T>,
): Promise<T | null> {
  // Try R2 first for durable cloud storage; fall back to IndexedDB; then none
  let blobStored = false;
  try {
    const r2Id = await putKnowledgeBlobToR2(item.id, blob);
    item.r2FileId = r2Id;
    item.storageLocation = 'r2';
    blobStored = true;
  } catch { /* R2 unavailable — try IndexedDB */ }
  if (!blobStored) {
    try {
      await putKnowledgeBlob(item.id, blob);
      item.storageLocation = 'indexeddb';
    } catch {
      item.storageLocation = 'none'; // quota exceeded — still try to capture text
    }
  }
  await putKnowledgeItem(item); // status 'processing' — visible immediately
  try {
    return await extract();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    item.processingStatus = 'failed';
    item.processingError = msg;
    item.processedAt = new Date().toISOString();
    await putKnowledgeItem(item);
    failJob(job, msg, item.id);
    return null;
  }
}

// ─── Capture: Files (PDF / documents / sheets / ebooks / text / audio) ───────

export function captureFiles(files: File[] | FileList): void {
  Array.from(files).forEach(file => {
    const job = newJob(file.name, 'file');
    enqueue(job, async j => {
      advance(j, 'capture', 4, 'Reading file…');

      if (file.size > MAX_FILE_BYTES) {
        throw new Error(
          `“${file.name}” is ${(file.size / 1024 / 1024).toFixed(0)} MB — the limit is 50 MB.`,
        );
      }

      const kind = detectFileKind(file);
      if (kind === 'unsupported') {
        throw new Error(
          `“${file.name}” is not a supported format. Supported: PDF, DOCX, XLSX/CSV, EPUB, TXT/MD, images, audio.`,
        );
      }
      if (kind === 'image') {
        // Route images through the photo pipeline (OCR).
        await runPhotoJob(j, file);
        return;
      }
      if (kind === 'audio') {
        await runAudioFileJob(j, file);
        return;
      }

      const bytes = await file.arrayBuffer();
      const hash = await sha256Hex(bytes);
      if (!(await claimSourceHash(j, hash))) return;

      advance(j, 'extract', 18, 'Extracting text…');
      const onProgress = (detail: string) => advance(j, 'extract', Math.min(44, j.pct + 2), detail);

      let extracted;
      let contentType: KnowledgeContentType;
      switch (kind) {
        case 'pdf':
          extracted = await extractPdf(file, onProgress);
          contentType = 'pdf';
          break;
        case 'docx':
          extracted = await extractDocx(file);
          contentType = 'document';
          break;
        case 'sheet':
          extracted = await extractSheet(file);
          contentType = 'spreadsheet';
          break;
        case 'epub':
          extracted = await extractEpub(file, onProgress);
          contentType = 'ebook';
          break;
        default:
          extracted = await extractPlainText(file);
          contentType = 'text';
      }

      if (!extracted.text || extracted.text.trim().length < 10) {
        throw new Error(
          `No readable text found in “${file.name}”. If it is a scanned document, try the Photo button instead — it runs OCR.`,
        );
      }

      const item = buildBaseItem({
        sourceType: 'file',
        contentType,
        title: extracted.docTitle || file.name.replace(/\.[^.]+$/, ''),
        originalSource: file.name,
        rawContent: extracted.text,
      });
      item.fileName = file.name;
      item.fileSize = file.size;
      item.fileMimeType = file.type || null;
      item.fileType = fileExtension(file);
      item.pageCount = extracted.pageCount;
      item.thumbnailUrl = extracted.thumbnailDataUrl;
      item.sourceHash = hash;

      await understandOrganizeStore(j, item, file);
    });
  });
}

// ─── Capture: Audio files (uploads) ──────────────────────────────────────────

async function runAudioFileJob(job: CaptureJob, file: File): Promise<void> {
  const bytes = await file.arrayBuffer();
  const hash = await sha256Hex(bytes);
  if (!(await claimSourceHash(job, hash))) return;

  if (file.size > 25 * 1024 * 1024) {
    throw new Error('Audio transcription is limited to 25 MB (~30–45 min). Try a shorter file.');
  }

  const item = buildBaseItem({
    sourceType: 'file',
    contentType: 'audio',
    title: file.name.replace(/\.[^.]+$/, ''),
    originalSource: file.name,
    rawContent: '',
  });
  item.fileName = file.name;
  item.fileSize = file.size;
  item.fileMimeType = file.type || null;
  item.fileType = fileExtension(file);
  item.sourceHash = hash;

  advance(job, 'extract', 20, 'Transcribing audio — this can take a minute…');
  const text = await extractWithSafetyNet(job, item, file, async () => {
    const dataUrl = await blobToDataUrl(file);
    return (await api<{ text: string }>('transcribe', { audioBase64: dataUrl }, 300_000)).text;
  });
  if (text === null) return;

  item.rawContent = text;
  item.wordCount = countWords(text);
  item.readingTimeMin = estimateReadingTimeMin(item.wordCount);

  await understandOrganizeStore(job, item, null);
}

// ─── Capture: Website ─────────────────────────────────────────────────────────

interface ExtractUrlResponse {
  url: string;
  title: string;
  byline: string | null;
  siteName: string | null;
  excerpt: string;
  textContent: string;
  contentHtml: string | null;
  heroImage: string | null;
  wordCount: number;
}

export function captureWebsite(url: string): void {
  const job = newJob(url, 'website');
  enqueue(job, async j => {
    advance(j, 'capture', 6, 'Contacting site…');
    const hash = await sha256Hex(`url:${url.trim().toLowerCase()}`);
    if (!(await claimSourceHash(j, hash))) return;

    advance(j, 'extract', 20, 'Extracting readable article…');
    const page = await api<ExtractUrlResponse>('extract-url', { url });

    const item = buildBaseItem({
      sourceType: 'website',
      contentType: 'website',
      title: page.title,
      originalSource: page.url,
      rawContent: page.textContent,
    });
    item.publisher = page.siteName ?? page.byline;
    item.contentHtml = page.contentHtml;
    item.thumbnailUrl = page.heroImage;
    item.sourceHash = hash;

    await understandOrganizeStore(j, item, null);
  });
}

// ─── Capture: YouTube ─────────────────────────────────────────────────────────

interface YouTubeResponse {
  videoId: string;
  url: string;
  title: string;
  channel: string | null;
  thumbnailUrl: string;
  description: string;
  durationSec: number | null;
  transcript: string;
  transcriptAvailable: boolean;
}

export function captureYouTube(url: string): void {
  const job = newJob(url, 'youtube');
  enqueue(job, async j => {
    advance(j, 'capture', 6, 'Looking up video…');
    const yt = await api<YouTubeResponse>('youtube', { url });

    const hash = await sha256Hex(`yt:${yt.videoId}`);
    if (!(await claimSourceHash(j, hash))) return;

    advance(j, 'extract', 30, yt.transcriptAvailable ? 'Transcript retrieved' : 'No transcript — using description');

    const rawContent = yt.transcriptAvailable
      ? yt.transcript
      : yt.description.trim();

    if (!rawContent || rawContent.length < 20) {
      throw new Error(
        'This video has no transcript or description to capture. Videos need captions enabled for full capture.',
      );
    }

    const item = buildBaseItem({
      sourceType: 'youtube',
      contentType: 'youtube',
      title: yt.title,
      originalSource: yt.url,
      rawContent: yt.transcriptAvailable
        ? rawContent
        : `[No transcript available — captured the video description instead]\n\n${rawContent}`,
    });
    item.publisher = yt.channel;
    item.thumbnailUrl = yt.thumbnailUrl;
    item.durationSec = yt.durationSec;
    item.sourceHash = hash;

    await understandOrganizeStore(j, item, null);
  });
}

// ─── Capture: Podcast ─────────────────────────────────────────────────────────

export interface PodcastFeedInfo {
  feedUrl: string;
  title: string;
  description: string;
  imageUrl: string | null;
  episodes: PodcastEpisodeInfo[];
}

export interface PodcastEpisodeInfo {
  index: number;
  title: string;
  pubDate: string | null;
  durationText: string | null;
  audioUrl: string | null;
  audioBytes: number | null;
  description: string;
  link: string | null;
}

/** Browse a feed (called by the capture modal — not a job). */
export async function fetchPodcastFeed(input: string): Promise<PodcastFeedInfo> {
  const isApple = input.includes('podcasts.apple.com');
  return api<PodcastFeedInfo>('podcast', isApple ? { appleUrl: input } : { feedUrl: input }, 45_000);
}

export function capturePodcastEpisode(show: PodcastFeedInfo, ep: PodcastEpisodeInfo): void {
  const job = newJob(`${ep.title}`, 'podcast');
  enqueue(job, async j => {
    advance(j, 'capture', 5, 'Preparing episode…');
    const hash = await sha256Hex(`podcast:${ep.audioUrl ?? `${show.feedUrl}#${ep.title}`}`);
    if (!(await claimSourceHash(j, hash))) return;

    let transcript = '';
    let transcriptFailed: string | null = null;

    if (ep.audioUrl) {
      advance(j, 'extract', 15, 'Downloading & transcribing — long episodes take a few minutes…');
      try {
        const { text } = await api<{ text: string }>('transcribe', { audioUrl: ep.audioUrl }, 300_000);
        transcript = text;
      } catch (err) {
        transcriptFailed = err instanceof Error ? err.message : String(err);
      }
    } else {
      transcriptFailed = 'This episode has no downloadable audio in the feed.';
    }

    const fallbackNotes = [ep.description, show.description].filter(Boolean).join('\n\n');
    const rawContent = transcript
      ? transcript
      : `[Transcript unavailable: ${transcriptFailed} — captured the episode notes instead]\n\n${fallbackNotes}`;

    if (!transcript && fallbackNotes.trim().length < 20) {
      throw new Error(transcriptFailed ?? 'Nothing could be captured for this episode.');
    }

    const item = buildBaseItem({
      sourceType: 'podcast',
      contentType: 'podcast',
      title: ep.title,
      originalSource: ep.link ?? ep.audioUrl ?? show.feedUrl,
      rawContent,
    });
    item.publisher = show.title;
    item.thumbnailUrl = show.imageUrl;
    item.sourceHash = hash;
    const durMatch = ep.durationText ? /^(?:(\d+):)?(\d+):(\d+)$/.exec(ep.durationText.trim()) : null;
    if (durMatch) {
      const [, h, m, s] = durMatch;
      item.durationSec = (h ? Number(h) * 3600 : 0) + Number(m) * 60 + Number(s);
    } else if (ep.durationText && /^\d+$/.test(ep.durationText.trim())) {
      item.durationSec = Number(ep.durationText.trim());
    }

    await understandOrganizeStore(j, item, null);
  });
}

// ─── Capture: Voice note ──────────────────────────────────────────────────────

export function captureVoiceNote(audioBlob: Blob, durationSec: number): void {
  const ts = new Date();
  const title = `Voice Note — ${ts.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${ts.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  const job = newJob(title, 'voice');
  enqueue(job, async j => {
    advance(j, 'capture', 8, 'Preparing recording…');
    if (audioBlob.size < 1000) {
      throw new Error('The recording is empty — hold the mic a bit longer and try again.');
    }

    const item = buildBaseItem({
      sourceType: 'voice',
      contentType: 'voice',
      title,
      originalSource: null,
      rawContent: '',
    });
    item.durationSec = Math.round(durationSec);
    item.fileSize = audioBlob.size;
    item.fileMimeType = audioBlob.type || 'audio/webm';

    advance(j, 'extract', 22, 'Transcribing your voice…');
    const text = await extractWithSafetyNet(j, item, audioBlob, async () => {
      const dataUrl = await blobToDataUrl(audioBlob);
      return (await api<{ text: string }>('transcribe', { audioBase64: dataUrl }, 180_000)).text;
    });
    if (text === null) return;

    item.rawContent = text;
    item.wordCount = countWords(text);
    item.readingTimeMin = estimateReadingTimeMin(item.wordCount);

    await understandOrganizeStore(j, item, null);
  });
}

// ─── Capture: Photo (OCR via vision model) ───────────────────────────────────

export function capturePhotos(files: File[] | FileList): void {
  Array.from(files).forEach(file => {
    const job = newJob(file.name || 'Photo', 'photo');
    enqueue(job, j => runPhotoJob(j, file));
  });
}

async function runPhotoJob(job: CaptureJob, file: File): Promise<void> {
  advance(job, 'capture', 6, 'Reading image…');
  if (!file.type.startsWith('image/')) {
    throw new Error(`“${file.name}” is not an image file.`);
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error('Images are limited to 50 MB.');
  }

  const bytes = await file.arrayBuffer();
  const hash = await sha256Hex(bytes);
  if (!(await claimSourceHash(job, hash))) return;

  const ts = new Date();
  const item = buildBaseItem({
    sourceType: 'photo',
    contentType: 'photo',
    title:
      file.name.replace(/\.[^.]+$/, '') ||
      `Photo — ${ts.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
    originalSource: file.name || null,
    rawContent: '',
  });
  item.fileName = file.name || null;
  item.fileSize = file.size;
  item.fileMimeType = file.type;
  item.fileType = fileExtension(file);
  item.thumbnailUrl = await makeImageThumbnail(file);
  item.sourceHash = hash;

  advance(job, 'extract', 20, 'Running OCR & image understanding…');
  const extracted = await extractWithSafetyNet(job, item, file, async () => {
    const dataUrl = await imageToDataUrl(file);
    const vision = await api<{ extractedText: string; description: string; hasText: boolean }>(
      'vision',
      { imageBase64: dataUrl },
      180_000,
    );
    const parts: string[] = [];
    if (vision.extractedText) parts.push(vision.extractedText);
    if (vision.description) parts.push(`[Image description] ${vision.description}`);
    if (parts.length === 0) {
      throw new Error('Nothing recognizable was found in this image. You can retry from the item view.');
    }
    return {
      text: parts.join('\n\n'),
      ocrTitle: vision.hasText && vision.extractedText
        ? vision.extractedText.split('\n')[0].slice(0, 60)
        : null,
    };
  });
  if (extracted === null) return;

  if (extracted.ocrTitle) item.title = extracted.ocrTitle;
  item.rawContent = extracted.text;
  item.wordCount = countWords(extracted.text);
  item.readingTimeMin = estimateReadingTimeMin(item.wordCount);

  await understandOrganizeStore(job, item, null);
}

// ─── Capture: Note ────────────────────────────────────────────────────────────

export function captureNote(params: { title: string; markdown: string }): void {
  const title = params.title.trim() || params.markdown.trim().split('\n')[0].slice(0, 60) || 'Untitled note';
  const job = newJob(title, 'note');
  enqueue(job, async j => {
    advance(j, 'capture', 10, 'Saving note…');
    const markdown = params.markdown.trim();
    if (!markdown) throw new Error('The note is empty.');

    const item = buildBaseItem({
      sourceType: 'note',
      contentType: 'note',
      title,
      originalSource: null,
      rawContent: markdown,
    });

    await understandOrganizeStore(j, item, null);
  });
}

// ─── Capture: Link bar (auto-detect) ─────────────────────────────────────────

export type LinkKind = 'youtube' | 'podcast-feed' | 'website';

export function detectLinkKind(raw: string): LinkKind {
  try {
    const u = new URL(/^https?:\/\//i.test(raw.trim()) ? raw.trim() : `https://${raw.trim()}`);
    const host = u.hostname.replace(/^www\.|^m\./, '');
    if (host === 'youtu.be' || host.endsWith('youtube.com')) return 'youtube';
    if (host === 'podcasts.apple.com') return 'podcast-feed';
    const path = u.pathname.toLowerCase();
    if (path.endsWith('.rss') || path.endsWith('.xml') || /\/(feed|rss)(\/|$)/.test(path)) {
      return 'podcast-feed';
    }
    return 'website';
  } catch {
    return 'website';
  }
}

/**
 * Route a pasted link. YouTube/website enqueue immediately; podcast feeds
 * need the episode picker, so the caller gets 'podcast-feed' back and opens
 * the podcast panel with the URL pre-filled.
 */
export function captureLink(url: string): LinkKind {
  const kind = detectLinkKind(url);
  if (kind === 'youtube') captureYouTube(url);
  else if (kind === 'website') captureWebsite(url);
  return kind;
}

// ─── Reprocess (retry AI analysis on a stored item) ──────────────────────────

export function reprocessItem(itemId: string): void {
  enqueueReprocess(itemId);
}

function enqueueReprocess(itemId: string): void {
  const job = newJob('Reprocessing…', 'note');
  enqueue(job, async j => {
    const item = await getKnowledgeItem(itemId);
    if (!item) throw new Error('This item no longer exists.');
    j.label = `Reprocess: ${item.title.slice(0, 55)}`;
    j.sourceType = item.sourceType;

    await updateKnowledgeItem(itemId, { processingStatus: 'processing', processingError: null });
    const fresh = (await getKnowledgeItem(itemId))!;

    // Voice/audio/photo items that failed during transcription/OCR have no
    // text yet — re-run extraction from the stored original blob first.
    if (
      fresh.rawContent.trim().length < 10 &&
      ['voice', 'audio', 'photo'].includes(fresh.contentType)
    ) {
      // Try R2 first (Sprint 091 migration), fall back to IndexedDB for legacy items
      let blob: Blob | null = null;
      if (fresh.r2FileId && fresh.storageLocation === 'r2') {
        blob = await getKnowledgeBlobFromR2(fresh.r2FileId).catch(() => null);
      }
      if (!blob) {
        blob = await getKnowledgeBlob(itemId);
      }
      if (!blob) {
        const msg = 'The original recording/photo is no longer stored, so this item cannot be reprocessed.';
        await updateKnowledgeItem(itemId, { processingStatus: 'failed', processingError: msg });
        failJob(j, msg, itemId);
        return;
      }
      advance(j, 'extract', 20, fresh.contentType === 'photo' ? 'Re-running OCR…' : 'Re-transcribing audio…');
      try {
        if (fresh.contentType === 'photo') {
          const dataUrl = await imageToDataUrl(blob);
          const vision = await api<{ extractedText: string; description: string; hasText: boolean }>(
            'vision',
            { imageBase64: dataUrl },
            180_000,
          );
          const parts: string[] = [];
          if (vision.extractedText) parts.push(vision.extractedText);
          if (vision.description) parts.push(`[Image description] ${vision.description}`);
          if (parts.length === 0) throw new Error('Nothing recognizable was found in this image.');
          fresh.rawContent = parts.join('\n\n');
        } else {
          const dataUrl = await blobToDataUrl(blob);
          const { text } = await api<{ text: string }>('transcribe', { audioBase64: dataUrl }, 300_000);
          fresh.rawContent = text;
        }
        fresh.wordCount = countWords(fresh.rawContent);
        fresh.readingTimeMin = estimateReadingTimeMin(fresh.wordCount);
        await putKnowledgeItem(fresh);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        await updateKnowledgeItem(itemId, { processingStatus: 'failed', processingError: msg });
        failJob(j, msg, itemId);
        return;
      }
    }

    advance(j, 'understand', 40, 'Re-running AI analysis…');
    await understandOrganizeStore(j, fresh, null);
  });
}
